import React, { useState, useEffect, useCallback } from 'react';
import { Ticket } from 'lucide-react';
import { SubscriptionVoucherResource, AvailableSubscriptionResource } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import { useAuth } from '../contexts/AuthContext';

const MONTHS = [1, 3, 6, 12] as const;

const statusBadge = (s: string) => {
  const map: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    used: 'bg-blue-100 text-blue-700',
    expired: 'bg-gray-100 text-gray-600',
    revoked: 'bg-red-100 text-red-700',
  };
  return map[s] || 'bg-gray-100 text-gray-600';
};

const SubscriptionVouchers: React.FC = () => {
  const { canManage } = useAuth();
  const [vouchers, setVouchers] = useState<SubscriptionVoucherResource[]>([]);
  const [subscriptions, setSubscriptions] = useState<AvailableSubscriptionResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const [form, setForm] = useState({ subscriptionId: '', months: 1 as 1 | 3 | 6 | 12, email: '', expiresInDays: 90 });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getSubscriptionVouchers(page, perPage, search || undefined);
      setVouchers(res.data);
      setTotal(res.meta.total);
    } catch (e) {
      console.error('Error loading vouchers:', e);
      setVouchers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.getSubscriptions(1, 100).then((res) => {
      const s = res.data;
      setSubscriptions(s);
      if (s.length && !form.subscriptionId) setForm((f) => ({ ...f, subscriptionId: s[0].id }));
    }).catch(() => setSubscriptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.subscriptionId || !form.email) { setError('Paket dan email wajib diisi.'); return; }
    try {
      setSubmitting(true);
      await api.generateSubscriptionVoucher({
        subscriptionId: form.subscriptionId,
        months: form.months,
        email: form.email.trim(),
        expiresInDays: form.expiresInDays,
      });
      setForm((f) => ({ ...f, email: '' }));
      setPage(1);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Gagal membuat voucher.');
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (id: string) => {
    if (!window.confirm('Batalkan voucher ini?')) return;
    try { await api.revokeSubscriptionVoucher(id); await load(); }
    catch (e: any) { window.alert(e?.response?.data?.message || 'Gagal membatalkan.'); }
  };

  const resend = async (id: string) => {
    try { await api.resendSubscriptionVoucher(id); window.alert('Email voucher dikirim ulang.'); }
    catch (e: any) { window.alert(e?.response?.data?.message || 'Gagal mengirim ulang.'); }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-6">
        <Ticket className="h-6 w-6 text-primary-600" />
        <h1 className="text-2xl font-bold text-gray-900">Subscription Vouchers</h1>
      </div>

      {canManage && (
      <form onSubmit={submit} className="mb-6 bg-white p-4 rounded-lg shadow grid grid-cols-1 sm:grid-cols-5 gap-3 sm:items-end">
        <div className="sm:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Paket</label>
          <select value={form.subscriptionId} onChange={(e) => setForm((f) => ({ ...f, subscriptionId: e.target.value }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2">
            {subscriptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Durasi (bulan)</label>
          <select value={form.months} onChange={(e) => setForm((f) => ({ ...f, months: Number(e.target.value) as 1 | 3 | 6 | 12 }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2">
            {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="sm:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="buyer@example.com" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Expiry (hari)</label>
          <input type="number" min={1} value={form.expiresInDays} onChange={(e) => setForm((f) => ({ ...f, expiresInDays: Number(e.target.value) || 90 }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2" />
        </div>
        <div>
          <button type="submit" disabled={submitting}
            className="w-full bg-primary-600 text-white rounded-md px-4 py-2 hover:bg-primary-700 disabled:opacity-50">
            {submitting ? 'Mengirim...' : 'Generate & Send'}
          </button>
        </div>
        {error && <div className="sm:col-span-5 text-sm text-red-600">{error}</div>}
      </form>
      )}

      <div className="mb-4">
        <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Cari email / kode..."
          className="w-full sm:w-80 border border-gray-300 rounded-md px-3 py-2" />
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2">Kode</th><th className="px-4 py-2">Email</th><th className="px-4 py-2">Paket</th>
                <th className="px-4 py-2">Bulan</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Company</th>
                <th className="px-4 py-2">Expiry</th><th className="px-4 py-2">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {vouchers.map((v) => (
                <tr key={v.id}>
                  <td className="px-4 py-2 font-mono">{v.couponCode}</td>
                  <td className="px-4 py-2">{v.email}</td>
                  <td className="px-4 py-2">{v.subscription?.name ?? '-'}</td>
                  <td className="px-4 py-2">{v.months}</td>
                  <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${statusBadge(v.status)}`}>{v.status}</span></td>
                  <td className="px-4 py-2">{v.company?.name ?? '-'}</td>
                  <td className="px-4 py-2">{v.expiredAt ? new Date(v.expiredAt).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {canManage && v.status === 'active' && (
                      <>
                        <button onClick={() => resend(v.id)} className="text-primary-600 hover:underline mr-3">Resend</button>
                        <button onClick={() => revoke(v.id)} className="text-red-600 hover:underline">Revoke</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {vouchers.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Belum ada voucher.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4">
        <Pagination currentPage={page} total={total} perPage={perPage} onPageChange={setPage} />
      </div>
    </div>
  );
};

export default SubscriptionVouchers;
